import { MutableRefObject, useEffect, useRef } from "react";

import { type DatatableState } from "@/domain/view/models/datatable-state-model";

export const DEFAULT_SAVE_DEBOUNCE_MS = 1000;

export const useDebouncedStateSave = ({
  state,
  saveDatatableState,
  skipNextSaveRef,
  debounceMs = DEFAULT_SAVE_DEBOUNCE_MS,
}: {
  state: DatatableState;
  saveDatatableState: (newDatatableState: DatatableState) => void;
  skipNextSaveRef: MutableRefObject<boolean>;
  debounceMs?: number;
}) => {
  const hasMountedRef = useRef(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const saveRef = useRef(saveDatatableState);
  saveRef.current = saveDatatableState;

  const clearPendingSave = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  };

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }

    if (skipNextSaveRef.current) {
      skipNextSaveRef.current = false;
      clearPendingSave();
      return;
    }

    clearPendingSave();
    debounceTimerRef.current = setTimeout(() => {
      saveRef.current(state);
      debounceTimerRef.current = null;
    }, debounceMs);
  }, [state, debounceMs, skipNextSaveRef]);

  useEffect(() => {
    return clearPendingSave;
  }, []);
};
